import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import axios from "axios";
import "../../assets/css/Steps.css";

function ContactList() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ like FocusList: show resolved list toggle
  const [showResolved, setShowResolved] = useState(false);

  const perPage = 10;
  const token = localStorage.getItem("profileToken");
  const baseURL = process.env.REACT_APP_API_URL;
  const headers = { "x-access-token": token || "" };

  // ✅ Fetch ALL contacts (same style as SubscriberList)
  useEffect(() => {
    const fetchContacts = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await axios.get(`${baseURL}/contact/all`, { headers });

        if (res.data?.status) {
          setContacts(res.data.result || []);
        } else {
          setContacts([]);
          setError(res.data?.message || "Failed to fetch contacts.");
        }
      } catch (err) {
        console.error("API Error:", err);
        setError("Failed to fetch contacts.");
      } finally {
        setLoading(false);
      }
    };

    fetchContacts();
    // eslint-disable-next-line
  }, [baseURL, token]);

  // ✅ counts
  const unsolvedCount = useMemo(
    () => contacts.filter((c) => Number(c.status) === 0).length,
    [contacts]
  );
  const resolvedCount = useMemo(
    () => contacts.filter((c) => Number(c.status) === 1).length,
    [contacts]
  );

  // ✅ filter by search (client-side)
  const filteredContacts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return contacts;

    return contacts.filter((c) => {
      const name = (c.name || "").toLowerCase();
      const email = (c.email || "").toLowerCase();
      const subject = (c.subject || "").toLowerCase();
      const idMatch = String(c.id) === q;
      return name.includes(q) || email.includes(q) || subject.includes(q) || idMatch;
    });
  }, [contacts, search]);

  // ✅ visible list (main = UNSOLVED, toggle = RESOLVED)
  const visibleContacts = useMemo(() => {
    return filteredContacts.filter((c) =>
      showResolved ? Number(c.status) === 1 : Number(c.status) === 0
    );
  }, [filteredContacts, showResolved]);

  // ✅ Pagination
  const totalPages = Math.ceil(visibleContacts.length / perPage) || 1;
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const currentContacts = visibleContacts.slice(indexOfFirst, indexOfLast);

  // ✅ reset to page 1 on search / toggle
  useEffect(() => {
    setCurrentPage(1);
  }, [search, showResolved]);

  // ✅ Selection
  const toggleSelect = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // ✅ Select All ONLY current page (same as SubscriberList)
  const toggleSelectAll = () => {
    const currentIds = currentContacts.map((c) => c.id);
    const allSelected = currentIds.length > 0 && currentIds.every((id) => selected.includes(id));

    if (allSelected) {
      setSelected((prev) => prev.filter((id) => !currentIds.includes(id)));
    } else {
      setSelected((prev) => [...new Set([...prev, ...currentIds])]);
    }
  };

  // ✅ Bulk mail
  const handleSendMail = () => {
    if (selected.length === 0) return Swal.fire("Error", "Select at least 1 contact", "error");

    const emails = contacts
      .filter((c) => selected.includes(c.id))
      .map((c) => c.email)
      .filter(Boolean);

    Swal.fire({
      title: "Send Mail",
      html: `Send mail to:<br/><b>${emails.join(", ")}</b>`,
      icon: "success",
    });
  };

  // ✅ View contact
  const handleView = (contact) => {
    Swal.fire({
      title: `<strong>${contact.name || "Contact"}</strong>`,
      html: `
        <p><b>Email:</b> ${contact.email || "-"}</p>
        <p><b>Phone:</b> ${contact.phone || "-"}</p>
        <p><b>Subject:</b> ${contact.subject || "-"}</p>
        <p><b>Message:</b> ${contact.message || "-"}</p>
        <p><b>Status:</b> ${Number(contact.status) === 1 ? "Resolved" : "Unsolved"}</p>
        <p><b>Created At:</b> ${
          contact.created_at ? new Date(contact.created_at).toLocaleString() : "-"
        }</p>
      `,
      icon: "info",
      confirmButtonText: "Close",
    });
  };

  // ✅ Delete
  const handleDelete = async (id) => {
    const contact = contacts.find((c) => c.id === id);

    const result = await Swal.fire({
      title: `Delete ${contact?.name || "contact"}?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.delete(`${baseURL}/contact/delete/${id}`, { headers });

      if (res.data?.status) {
        setContacts((prev) => prev.filter((c) => c.id !== id));
        setSelected((prev) => prev.filter((x) => x !== id));
        Swal.fire("Deleted!", "Contact has been removed.", "success");
      } else {
        Swal.fire("Error!", res.data?.message || "Failed to delete contact.", "error");
      }
    } catch (err) {
      console.error("Delete error:", err);
      Swal.fire("Error!", "An error occurred while deleting the contact.", "error");
    }
  };

  /**
   * ✅ Resolved / Unsolved rules:
   * - Main list shows UNSOLVED only (status 0)
   * - Resolved list shows only RESOLVED (status 1)
   * - You can mark UNSOLVED -> RESOLVED (with SweetAlert confirm)
   * - You CANNOT mark RESOLVED -> UNSOLVED (block + alert)
   */
  const markResolved = async (contact) => {
    if (Number(contact.status) === 1) {
      return Swal.fire("Not Allowed", "Resolved contact cannot be moved back to Unsolved.", "info");
    }

    const result = await Swal.fire({
      title: "Mark as Resolved?",
      text: "After resolving, it cannot be moved back to Unsolved.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, resolve it!",
    });

    if (!result.isConfirmed) return;

    try {
      const res = await axios.put(
        `${baseURL}/contact/status/${contact.id}`,
        { status: 1 },
        { headers }
      );

      if (res.data?.status) {
        setContacts((prev) =>
          prev.map((c) => (c.id === contact.id ? { ...c, status: 1 } : c))
        );

        // ✅ if currently in Unsolved view, item will disappear automatically
        Swal.fire({
          icon: "success",
          title: "Resolved",
          text: "Contact marked as Resolved",
          timer: 1200,
          showConfirmButton: false,
        });
      } else {
        Swal.fire("Error!", res.data?.message || "Failed to update status", "error");
      }
    } catch (err) {
      console.error("Status update error:", err);
      Swal.fire("Error!", "An error occurred while updating status", "error");
    }
  };

  return (
    <div className="container mt-4">
      {/* ✅ Header (same look/feel as SubscriberList) */}
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3 p-3 border rounded shadow-sm bg-white">
        <div>
          <h4 className="mb-1 text-success">
            {showResolved ? "Resolved Contacts" : "Unsolved Contacts"}
          </h4>
          <div className="text-muted" style={{ fontSize: 14 }}>
            Total:{" "}
            <span className="badge bg-secondary ms-1">
              {showResolved ? resolvedCount : unsolvedCount}
            </span>
            <span className="ms-2 badge bg-dark">Selected: {selected.length}</span>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2">
          {/* ✅ Toggle button like FocusList */}
          <button
            className={`btn ${
              showResolved ? "btn-outline-success" : "btn-outline-danger"
            } rounded-pill px-4`}
            onClick={() => {
              setShowResolved((p) => !p);
              setSelected([]); // optional: clear selection when switching tab
            }}
          >
            {showResolved
              ? `Unsolved Contacts (${unsolvedCount})`
              : `Resolved Contacts (${resolvedCount})`}
          </button>

          <button
            className="btn btn-success rounded-pill px-4"
            onClick={handleSendMail}
            disabled={selected.length === 0}
          >
            Send Mail
          </button>

          <button className="btn btn-secondary rounded-pill px-4" onClick={toggleSelectAll}>
            {currentContacts.length > 0 &&
            currentContacts.every((c) => selected.includes(c.id))
              ? "Deselect All"
              : "Select All"}
          </button>
        </div>
      </div>

      {/* ✅ Search */}
      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name, email, subject or ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-center my-5">Loading contacts...</div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : visibleContacts.length === 0 ? (
        <div className="alert alert-info">
          {showResolved ? "No resolved contacts found" : "No unsolved contacts found"}
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <table className="table table-striped table-bordered table-hover align-middle">
              <thead className="table-success">
                <tr>
                  <th style={{ width: 80 }}>Select</th>
                  <th style={{ width: 80 }}>#</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Subject</th>
                  <th>Phone</th>
                  <th style={{ width: 150 }}>Status</th>
                  <th style={{ width: 150 }}>Actions</th>
                </tr>
              </thead>

              <tbody>
                {currentContacts.map((c, index) => (
                  <tr
                    key={c.id}
                    className={Number(c.status) === 1 ? "table-secondary" : ""}
                    style={Number(c.status) === 1 ? { opacity: 0.92 } : {}}
                  >
                    <td className="text-center">
                      <input
                        type="checkbox"
                        checked={selected.includes(c.id)}
                        onChange={() => toggleSelect(c.id)}
                      />
                    </td>

                    <td>{indexOfFirst + index + 1}</td>
                    <td>{c.name || "-"}</td>
                    <td>{c.email || "-"}</td>
                    <td>{c.subject || "-"}</td>
                    <td>{c.phone || "-"}</td>

                    {/* ✅ Status column: switch ONLY for unsolved, resolved switch disabled */}
                    <td className="text-center">
                      <div className="form-check form-switch d-flex justify-content-center align-items-center gap-2">
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={Number(c.status) === 1}
                          disabled={Number(c.status) === 1} // ✅ resolved can't go back
                          onChange={() => {
                            // only allow unsolved -> resolved
                            markResolved(c);
                          }}
                        />
                        <span className="text-muted" style={{ fontSize: 13 }}>
                          {Number(c.status) === 1 ? "Resolved" : "Unsolved"}
                        </span>
                      </div>
                    </td>

                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-info me-2"
                        onClick={() => handleView(c)}
                        title="View"
                      >
                        <i className="fa fa-eye"></i>
                      </button>

                      <button
                        className="btn btn-sm btn-danger"
                        onClick={() => handleDelete(c.id)}
                        title="Delete"
                      >
                        <i className="fa fa-trash"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ✅ Pagination */}
          {totalPages > 1 && (
            <nav>
              <ul className="pagination">
                {Array.from({ length: totalPages }, (_, i) => (
                  <li
                    key={i + 1}
                    className={`page-item ${currentPage === i + 1 ? "active" : ""}`}
                  >
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                      {i + 1}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
          )}
        </>
      )}
    </div>
  );
}

export default ContactList;
